import { Amplify } from 'aws-amplify';
import {
  confirmSignIn,
  fetchAuthSession,
  signIn,
  signOut
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { atom, Atom } from 'jotai';
import { atomWithStorage, RESET } from 'jotai/utils';
import config from '~/config';
import { g } from '~/globals';

export interface AuthService {
  /**
   * Initialize the auth service
   */
  initialize(): Promise<void>;

  /**
   * Request OTP over the provided transport method
   *
   * @param email
   * @param method
   */
  requestOtp(email: string, method: 'SMS' | 'EMAIL'): Promise<void>;

  /**
   * Confirm OTP
   *
   * If this goes through, the user is logged in
   *
   * @param email
   * @param otp
   */
  confirmLogin(email: string, otp: string): Promise<void>;

  /**
   * Get the jwt when making backend requests
   */
  getJwt(): Promise<string | undefined>;

  /**
   * Sign out the current user
   */
  signOut(): Promise<void>;

  /**
   * Note: This is not a promise
   *
   * This is used in the individual components to check logged in state
   * It is likely just returning the value of an internal variable
   *
   * We use an atom to allow components using this value to be reactive
   */
  isLoggedIn(): Atom<boolean>;
}

export class CognitoAuth implements AuthService {
  private isLoggedInAtom = atomWithStorage('cognitoIsLoggedIt', false);

  async initialize(): Promise<void> {
    const cognitoConfig = {
      userPoolId: config.cognito.userPoolId,
      userPoolClientId: config.cognito.clientId,
      identityPoolId: config.cognito.identityPoolId
    };

    if (
      !cognitoConfig.userPoolId ||
      !cognitoConfig.userPoolClientId ||
      !cognitoConfig.identityPoolId
    ) {
      throw new Error('Could not initialize Cognito');
    }

    /**
     * BE AWARE!
     *
     * The package `aws-amplify` package is RELATED  with `@aws-amplify/ui-react-liveness`
     * When upgrading `aws-amplify` makes sure to update `@aws-amplify/ui-react-liveness` to void version drift.
     */
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: cognitoConfig.userPoolId,
          userPoolClientId: cognitoConfig.userPoolClientId,
          identityPoolId: cognitoConfig.identityPoolId
        }
      }
    });

    // This will listen to change in authentication state by Amplify component
    const self = this;
    Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn': {
          g.reg.jotaiStore.set(this.isLoggedInAtom, true);
          break;
        }
        case 'signedOut': {
          g.reg.jotaiStore.set(self.isLoggedInAtom, false);
          break;
        }

        // We don't really use these values, but they are written out to have a match on all values
        // TODO: Hook up appropriate logging / error management
        case 'customOAuthState':
          break;
        case 'signInWithRedirect':
          break;
        case 'tokenRefresh':
          break;
        case 'tokenRefresh_failure':
          break;
        case 'signInWithRedirect_failure':
          break;
      }
    });
  }

  async signOut(): Promise<void> {
    await signOut();
  }

  async requestOtp(email: string, method: 'SMS' | 'EMAIL'): Promise<void> {
    if (!config.cognito.clientId) {
      throw new Error('Cognito client id is not set');
    }

    /**
     * If user attempt to login when there is an existing user
     * Throw an error
     */
    const currentUser = await fetchAuthSession();
    if (currentUser.tokens?.accessToken) {
      throw new Error('Cannot log in when another user is logged in.');
    }

    await g.reg.tdcBackendClient.gqlMutationClient({
      dealershiplessAuth: true
    })({
      setDeliveryMethodForUser: [
        {
          email: email,
          method
        },
        {
          '...on MutationSetDeliveryMethodForUserSuccess': {
            data: {
              status: true
            }
          },
          '...on GraphQLError': {
            message: true
          }
        }
      ]
    });

    /**
     * When we call amplify `signIn` without a password authFlowType `CUSTOM_WITHOUT_SRP`
     * This automatically set the user into `Challenged` state
     *
     * This state will make Cognito automatically trigger the customized lambda trigger for Custom Challenge
     * See `cognitoCreateAuthChallenge` in Pulumi for more details
     */
    await signIn({
      username: email,
      options: {
        authFlowType: 'CUSTOM_WITHOUT_SRP',
        clientMetadata: {
          method
        }
      }
    });
  }
  async confirmLogin(email: string, otp: string): Promise<void> {
    // Submit the custom challange response
    const confirmRes = await confirmSignIn({
      challengeResponse: otp
    });

    // If failed to signIn throw an error
    // This indicates the Custom Challenge Answer is Invalid
    if (
      !confirmRes.isSignedIn &&
      confirmRes.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE'
    ) {
      throw new Error('Invalid OTP');
    }

    // We rely on the hub updating the atom at the event is fired
  }

  /**
   * Return jwt if there is any
   *
   * @returns
   */
  async getJwt(): Promise<string | undefined> {
    const session = await fetchAuthSession();
    return session.tokens?.accessToken.toString();
  }
  isLoggedIn() {
    return this.isLoggedInAtom;
  }
}

/**
 * Used for local development and E2E tests
 */
export class DEVAuth implements AuthService {
  // We just use atoms for internal storage, as that is what we need to expose for the isLoggedIn (For proper integration with React)
  private jwtAtom = atomWithStorage<string | undefined>('authJwt', undefined);
  private itLoggedInAtom: Atom<boolean> = atom((get) =>
    Boolean(get(this.jwtAtom))
  );

  async initialize(): Promise<void> {
    // Nothing to initialize
  }

  async signOut(): Promise<void> {
    // Merely reset the atom to logout
    g.reg.jotaiStore.set(this.jwtAtom, RESET);
  }

  async requestOtp(email: string): Promise<void> {
    const resp = await g.reg.tdcBackendClient.gqlMutationClient({
      dealershiplessAuth: true
    })({
      requestOtpExistingUserDEV: [
        {
          email
        },
        {
          __typename: true,
          '...on GraphQLError': {
            message: true
          },
          '...on MutationRequestOtpExistingUserDEVSuccess': {
            data: {
              status: true
            }
          }
        }
      ]
    });

    if (
      !resp.requestOtpExistingUserDEV ||
      resp.requestOtpExistingUserDEV.__typename === 'GraphQLError'
    ) {
      throw new Error(
        resp.requestOtpExistingUserDEV?.message ?? 'Unexpected error'
      );
    }
  }
  async confirmLogin(email: string, otp: string): Promise<void> {
    const resp = await g.reg.tdcBackendClient.gqlMutationClient({
      dealershiplessAuth: true
    })({
      verifyOtpForExistingUser: [
        {
          email,
          emailCode: otp
        },
        {
          __typename: true,
          '...on GraphQLError': {
            message: true
          },
          '...on MutationVerifyOtpForExistingUserSuccess': {
            data: {
              jwt: true
            }
          }
        }
      ]
    });

    if (
      !resp.verifyOtpForExistingUser ||
      resp.verifyOtpForExistingUser.__typename === 'GraphQLError'
    ) {
      throw new Error(
        resp.verifyOtpForExistingUser?.message ?? 'Unexpected error'
      );
    }

    if (!resp.verifyOtpForExistingUser.data.jwt) {
      throw new Error('No JWT returned');
    } else {
      g.reg.jotaiStore.set(
        this.jwtAtom,
        resp.verifyOtpForExistingUser.data.jwt
      );
    }
  }
  async getJwt(): Promise<string | undefined> {
    return g.reg.jotaiStore.get(this.jwtAtom);
  }
  isLoggedIn() {
    return this.itLoggedInAtom;
  }
}
